import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const Profile = ({ userProfile, setUserProfile }) => {
  const [loading, setLoading] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  
  const [profileData, setProfileData] = useState({
    civilite: 'M.',
    prenom: '',
    nom: '',
    email: '',
    telephone: '',
    photo_url: ''
  });

  // Load profile on mount
  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const response = await axios.get(`${API}/api/settings/user`);
      setProfileData({
        civilite: response.data.civilite || 'M.',
        prenom: response.data.prenom || '',
        nom: response.data.nom || '',
        email: response.data.email || '',
        telephone: response.data.telephone || '',
        photo_url: response.data.photo_url || ''
      });
    } catch (error) {
      console.error('Erreur lors du chargement du profil:', error);
    }
  };

  const handleChange = (field, value) => {
    setProfileData(prev => ({ ...prev, [field]: value }));
  };

  const saveProfile = async () => {
    setLoading(true);
    setSaveMessage('');
    try {
      // Get current settings to preserve theme, langue, notifications
      const currentSettings = await axios.get(`${API}/api/settings/user`);
      
      // Merge profile data with existing settings
      const updatedSettings = {
        ...currentSettings.data,
        civilite: profileData.civilite,
        prenom: profileData.prenom,
        nom: profileData.nom,
        email: profileData.email,
        telephone: profileData.telephone,
        photo_url: profileData.photo_url
      };
      
      await axios.put(`${API}/api/settings/user`, updatedSettings);
      
      // Update userProfile in App.js
      if (setUserProfile) {
        setUserProfile({
          civilite: profileData.civilite,
          prenom: profileData.prenom,
          nom: profileData.nom,
          email: profileData.email,
          telephone: profileData.telephone,
          ...userProfile
        });
      }
      
      setSaveMessage('✅ Profil mis à jour avec succès!');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      setSaveMessage('❌ Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">Mon Profil</h1>
            <p className="text-gray-600 mt-2">Gérez vos informations personnelles</p>
          </div>

          {/* Save Message */}
          {saveMessage && (
            <div className={`mb-4 p-4 rounded-lg ${
              saveMessage.includes('✅') ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
            }`}>
              {saveMessage}
            </div>
          )}

          {/* Profile Form */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Civilité *
                </label>
                <select
                  value={profileData.civilite || 'M.'}
                  onChange={(e) => handleChange('civilite', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="M.">Monsieur (M.)</option>
                  <option value="Mme">Madame (Mme)</option>
                  <option value="Mlle">Mademoiselle (Mlle)</option>
                  <option value="Dr">Docteur (Dr)</option>
                  <option value="Me">Maître (Me)</option>
                </select>
              </div>

              <div className="md:col-span-2"></div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Prénom *
                </label>
                <input
                  type="text"
                  value={profileData.prenom || ''}
                  onChange={(e) => handleChange('prenom', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Votre prénom"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom *
                </label>
                <input
                  type="text"
                  value={profileData.nom || ''}
                  onChange={(e) => handleChange('nom', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Votre nom"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={profileData.email || ''}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="votre.email@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Téléphone
                </label>
                <input
                  type="tel"
                  value={profileData.telephone || ''}
                  onChange={(e) => handleChange('telephone', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="+33 X XX XX XX XX"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  URL Photo de profil
                </label>
                <input
                  type="url"
                  value={profileData.photo_url || ''}
                  onChange={(e) => handleChange('photo_url', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="https://..."
                />
                <p className="text-xs text-gray-500 mt-1">URL de votre photo de profil (optionnel)</p>
              </div>
            </div>

            <div className="mt-8 flex justify-end">
              <button
                onClick={saveProfile}
                disabled={loading}
                className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:bg-gray-400"
              >
                {loading ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </div>
          </div>
      </div>
    </div>
  );
};

export default Profile;
